/**
 * The single source of truth for which routes Vercel BotID protects, and at
 * what depth.
 *
 * 🚨 WHY THIS FILE EXISTS: BotID's client declaration and the server's
 * `checkBotId()` call must agree on `checkLevel` for every protected route. A
 * mismatch does not error — verification just fails, which reads as "every
 * visitor is a bot" and silently switches the protected feature off. Declaring
 * the level once and importing it on both sides makes that mismatch
 * unrepresentable rather than merely unlikely.
 *
 * Client side: `initBotId({ protect: BOTID_PROTECTED_ROUTES })` from
 * components/botid-boot.tsx, mounted first in the root <body>. Not the
 * package's <BotIdClient> in <head>: its inline script never runs on a shell
 * React renders on the client (the 404, error.tsx), see that component.
 * Server side: `checkBotId(BOTID_CHECK_CONFIG)` in the route handler.
 *
 * Adding a route here is not enough on its own — the handler must also call
 * `checkBotId`, and a route the client does not declare cannot be verified at
 * all (`checkBotId` fails for it).
 *
 * Deliberately dependency-free so the client bundle picks up nothing but two
 * literals.
 */

/** Deep Analysis (Kasada) rather than 'basic': the traffic this is aimed at is
 *  real headless Chrome behind residential proxies, which passes every
 *  integrity-only check by construction. Billed per `checkBotId()` call, so
 *  handlers must exhaust their free checks before reaching for it. */
const CHECK_LEVEL = 'deepAnalysis' as const;

/** The free level: Vercel's own client checks, no Kasada call. For the one
 *  route whose answer costs less than a deep verdict would. */
const BASIC_CHECK_LEVEL = 'basic' as const;

/** Routes whose requests the client instruments. */
export const BOTID_PROTECTED_ROUTES = [
  {
    path: '/api/offers/visit',
    method: 'POST',
    advancedOptions: { checkLevel: CHECK_LEVEL },
  },
  {
    path: '/api/product-search',
    method: 'POST',
    advancedOptions: { checkLevel: CHECK_LEVEL },
  },
  {
    // Judged only when the request carries no verified search challenge: the
    // shelf exists because a search ran, and that search was judged already.
    path: '/api/search/shelf-refresh',
    method: 'POST',
    advancedOptions: { checkLevel: CHECK_LEVEL },
  },
  {
    path: '/api/classify-intent',
    method: 'POST',
    advancedOptions: { checkLevel: CHECK_LEVEL },
  },
  {
    // Judged only when the request carries no search proof yet: the first
    // link a shopper pastes, before any search has run.
    path: '/api/link-preview',
    method: 'POST',
    advancedOptions: { checkLevel: CHECK_LEVEL },
  },
  {
    // A question put to an article's reader: one short Haiku call, behind a
    // rate limit. The basic level is not billed, and the deep one would cost
    // more than the answer it guards. Declared basic on both sides, like the
    // deep routes are declared deep (see the header comment).
    path: '/api/articles/ask',
    method: 'POST',
    advancedOptions: { checkLevel: BASIC_CHECK_LEVEL },
  },
  {
    // A clip of the narrator's voice that is not rendered yet (rendered clips
    // come straight from storage). Basic, like /api/articles/ask: the route
    // only renders text we published, so a deep verdict would guard little.
    path: '/api/narrator/speech',
    method: 'POST',
    advancedOptions: { checkLevel: BASIC_CHECK_LEVEL },
  },
] as const;

/** The matching server-side argument. Pass this to `checkBotId()`. */
export const BOTID_CHECK_CONFIG = {
  advancedOptions: { checkLevel: CHECK_LEVEL },
} as const;

/** Server-side argument for the routes declared at the basic level above. */
export const BOTID_BASIC_CHECK_CONFIG = {
  advancedOptions: { checkLevel: BASIC_CHECK_LEVEL },
} as const;

/**
 * A verdict that proves a person. A firewall bypass rule answers `bypassed`
 * without judging anyone, so it only counts outside production (local
 * development returns a bypassed verdict by design).
 */
export function isHumanVerdict(verdict: { isHuman: boolean; bypassed: boolean }): boolean {
  if (!verdict.isHuman) return false;
  return !verdict.bypassed || process.env.NODE_ENV !== 'production';
}

/**
 * Response header on an admitted `/api/product-search` turn, and request
 * header on `/api/check-region-needed`: the proof
 * that this browser already cleared the BotID gate, so the paid helpers never
 * answer a refused bot. Lives here, dependency-free, because the search bar
 * reads it too. Minted and checked in server/search/search-helper-pass.ts.
 */
export const SEARCH_HELPER_PASS_HEADER = 'x-search-pass';
